import React, { Fragment, FC, useState } from 'react';
import { Button } from '@material-ui/core';
import { createStyles, withStyles, lighten, Theme, WithStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MutationResult, MutationFunction } from 'react-apollo';
import { DataProxy } from 'apollo-cache';
import { useNotify } from 'hooks';

import { DeleteScheduledSharesComponent, SharesItem } from 'api';

import { Alert, DeleteIcon } from 'components';

interface IProps extends WithStyles<typeof styles> {
  items: SharesItem[];
  onDelete: (cache: DataProxy, result: MutationResult['data']) => void;
}

const styles = (theme: Theme) =>
  createStyles({
    deleteButton: {
      width: 140,
      justifyContent: 'flex-start',
      '&:hover': {
        borderColor: theme.palette.error.main,
        backgroundColor: lighten(theme.palette.error.main, 0.9),
        '& svg': {
          color: theme.palette.error.main,
        },
      },
    },
  });

const DeleteShare: FC<IProps> = ({ classes, items, onDelete }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  function handleNotification() {
    notify.info({ message: t('posting:deleteScheduledShareSuccess') });
  }

  function handleDelete(mutation: MutationFunction) {
    setOpen(false);
    const ids = items.map(item => item.id);
    return mutation({
      variables: {
        ids,
      },
      update: onDelete,
    });
  }

  function renderConfirmation() {
    return (
      <DeleteScheduledSharesComponent
        onError={notify.mutationError}
        onCompleted={handleNotification}>
        {mutationFn => (
          <Alert
            open={open}
            onConfirm={() => handleDelete(mutationFn)}
            onCancel={() => setOpen(false)}
            message={t('posting:deleteScheduledShareMessage')}
            confirm={t('common:delete')}
          />
        )}
      </DeleteScheduledSharesComponent>
    );
  }

  function renderButton() {
    return (
      <Button
        fullWidth
        variant="outlined"
        size="small"
        className={classes.deleteButton}
        startIcon={<DeleteIcon iconProps={{ color: 'disabled' }} />}
        onClick={() => setOpen(true)}>
        {t('sharing:deleteShare')}
      </Button>
    );
  }

  return (
    <Fragment>
      {renderButton()}
      {renderConfirmation()}
    </Fragment>
  );
};

export default withStyles(styles)(DeleteShare);
